/**
 * Reports — gráficos e analytics (recharts).
 */
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Star, TrendingUp } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonCard } from "@/components/ui/skeleton";
import { reportsApi } from "@/lib/api-extras";
import { formatCurrency, formatDate } from "@/lib/format";

const PIE_COLORS = [
  "#1c97c8", "#2dd4bf", "#f59e0b", "#a855f7", "#ef4444",
  "#06b6d4", "#84cc16", "#ec4899", "#6366f1", "#10b981",
];

export function ReportsPage() {
  const revenue = useQuery({ queryKey: ["report-revenue"], queryFn: () => reportsApi.revenueMonthly(12) });
  const topClients = useQuery({ queryKey: ["report-top-clients"], queryFn: () => reportsApi.topClients(8) });
  const topNerds = useQuery({ queryKey: ["report-top-nerds"], queryFn: () => reportsApi.topNerdsRevenue(8) });
  const byCategory = useQuery({ queryKey: ["report-by-cat"], queryFn: () => reportsApi.jobsByCategory() });
  const sla = useQuery({ queryKey: ["report-sla"], queryFn: () => reportsApi.slaCompliance() });

  const revenueData = revenue.data?.map((r) => ({
    month: formatDate(r.month, "MMM/yy"),
    revenue: parseFloat(r.revenue),
    count: r.count,
  }));

  return (
    <>
      <PageHeader
        title="Relatórios"
        description="Métricas e análise de performance da operação."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {sla.isLoading ? <SkeletonCard /> : (
          <StatCard
            label="Cumprimento de SLA"
            value={`${sla.data?.compliance_pct ?? 0}%`}
            hint={`${sla.data?.on_time ?? 0} no prazo · ${sla.data?.late ?? 0} atrasados`}
            icon={TrendingUp}
            iconColor={sla.data && sla.data.compliance_pct >= 90 ? "success" : sla.data && sla.data.compliance_pct >= 70 ? "warning" : "danger"}
          />
        )}
        <StatCard
          label="Faturamento total (12m)"
          value={formatCurrency(
            revenue.data?.reduce((acc, r) => acc + parseFloat(r.revenue), 0)
          )}
          icon={BarChart3}
          iconColor="primary"
        />
        <StatCard
          label="Chamados (12m)"
          value={revenue.data?.reduce((acc, r) => acc + r.count, 0) ?? 0}
          icon={Star}
          iconColor="purple"
        />
      </div>

      {/* Revenue chart */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-6 mb-6">
        <h3 className="font-semibold text-base mb-1">Faturamento mensal</h3>
        <p className="text-xs text-muted-foreground mb-4">Últimos 12 meses · soma de chamados concluídos faturáveis</p>
        {revenue.isLoading ? (
          <SkeletonCard />
        ) : revenueData && revenueData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
              <YAxis
                stroke="#94a3b8"
                fontSize={11}
                tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(v) => formatCurrency(typeof v === "number" ? v : parseFloat(String(v)))}
                cursor={{ fill: "rgba(28, 151, 200, 0.06)" }}
                contentStyle={{
                  background: "rgba(255,255,255,0.95)",
                  border: "1px solid rgba(15,23,42,0.06)",
                  borderRadius: "0.75rem",
                  fontSize: "12px",
                }}
              />
              <Bar dataKey="revenue" fill="#1c97c8" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState title="Sem dados de faturamento" className="border-0 py-12" />
        )}
      </div>

      {/* Grid: top clients + by category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-6">
          <h3 className="font-semibold text-base mb-3">Top clientes</h3>
          {topClients.isLoading ? (
            <SkeletonCard />
          ) : (
            <ol className="space-y-2">
              {topClients.data?.map((c, idx) => (
                <li key={c.id} className="flex items-center gap-3 py-1.5">
                  <span className="text-xs font-bold tabular-nums text-muted-foreground w-5">{idx + 1}</span>
                  <Avatar size="sm">
                    <AvatarFallback name={c.trade_name} />
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{c.trade_name}</p>
                    <p className="text-[11px] text-muted-foreground">{c.jobs_count} chamados</p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{formatCurrency(c.revenue)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-6">
          <h3 className="font-semibold text-base mb-3">Chamados por categoria</h3>
          {byCategory.isLoading ? (
            <SkeletonCard />
          ) : byCategory.data && byCategory.data.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={byCategory.data}
                  dataKey="count"
                  nameKey="category"
                  outerRadius={80}
                  paddingAngle={2}
                  label={(d: { value?: number }) => `${d.value ?? ""}`}
                >
                  {byCategory.data.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout="vertical" verticalAlign="middle" align="right" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="Sem dados" className="border-0 py-8" />
          )}
        </div>
      </div>

      {/* Top nerds */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-6">
        <h3 className="font-semibold text-base mb-3">Top técnicos por repasse</h3>
        {topNerds.isLoading ? (
          <SkeletonCard />
        ) : (
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {topNerds.data?.map((n, idx) => (
              <li key={n.id} className="flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-slate-50/60 dark:hover:bg-white/[0.03]">
                <span className="text-xs font-bold tabular-nums text-muted-foreground w-5">{idx + 1}</span>
                <Avatar size="sm">
                  <AvatarFallback name={n.legal_name ?? ""} />
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{n.legal_name ?? "—"}</p>
                  <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
                    {n.rank_score}/100 · {n.jobs_count} jobs
                  </p>
                </div>
                <span className="text-sm font-semibold tabular-nums">{formatCurrency(n.payout)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <p className="mt-6 text-xs text-muted-foreground inline-flex items-center gap-1">
        <Badge variant="muted" size="sm">Beta</Badge>
        Dashboards mais detalhados (cohort, retention, NPS) em breve.
      </p>
    </>
  );
}
